import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FounderEditDrawer } from "../../../components/founder/FounderEditDrawer";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { FounderIconButton } from "../../../components/founder/FounderIconButton";
import { FounderOperationalShell } from "../../../components/founder/FounderOperationalShell";
import {
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  cancelFounderMembershipPlanPriceSchedule,
  createFounderMembershipPlan,
  fetchFounderMembershipPlanPriceSchedules,
  fetchFounderMembershipPlanPricingHistory,
  fetchFounderMembershipPlans,
  fetchFounderPayments,
  fetchFounderPaymentSummary,
  fetchFounderPublisherPaymentProviderStatus,
  fetchFounderSubscriptions,
  fetchFounderWorkspaceCounts,
  interveneFounderSubscription,
  publishFounderMembershipPlanPricing,
  updateFounderMembershipPlan,
  type FounderMembershipPlanRow,
  type FounderMembershipPricingHistoryRow,
  type FounderPaymentRow,
  type FounderPaymentSummary,
  type FounderPriceScheduleRow,
  type FounderSubscriptionRow,
} from "../../../founder/founderWorkspaceApi";
import { validatePublisherMembershipPriceInput } from "../../../services/publicMembershipPrice";

type ApplyToOption = "new_memberships_only" | "new_and_renewals";

type PlanDraft = {
  name: string;
  newPriceAmount: string;
  effectiveFrom: string;
  applyTo: ApplyToOption;
  renewalIncreasePercent: string;
  isActive: boolean;
};

function planPriceValue(plan: FounderMembershipPlanRow): string {
  return String(plan.price_amount);
}

function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function toPlanDraft(plan: FounderMembershipPlanRow): PlanDraft {
  return {
    name: plan.name,
    newPriceAmount: planPriceValue(plan),
    effectiveFrom: todayIsoDate(),
    applyTo: "new_memberships_only",
    renewalIncreasePercent: String(plan.renewal_increase_percent),
    isActive: plan.is_active,
  };
}

function formatPlanPrice(amount: string | number | null, currency: string): string {
  if (amount === null || amount === undefined) {
    return "—";
  }
  const value = Number(amount);
  if (Number.isNaN(value)) {
    return `${currency} ${amount}`;
  }
  if (currency === "INR") {
    return `₹${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
  return `${currency} ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string | null): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(iso: string | null): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function paymentPurposeLabel(purpose: string | null): string {
  if (!purpose) {
    return "Not recorded";
  }
  if (purpose === "initial_subscription") {
    return "Membership";
  }
  if (purpose === "renewal") {
    return "Renewal";
  }
  return purpose.replaceAll("_", " ");
}

function paymentStatusLabel(status: string): string {
  if (status === "succeeded") {
    return "Successful";
  }
  if (status === "processing") {
    return "Pending";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function unavailable(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "Not recorded";
  }
  return String(value);
}

function renewalPercent(plan: FounderMembershipPlanRow): number {
  return Number(plan.renewal_increase_percent);
}

export function FounderMembershipsWorkspace() {
  const [searchParams] = useSearchParams();
  const initialPaymentStatus = searchParams.get("paymentStatus") ?? "all";

  const [counts, setCounts] = useState<Awaited<ReturnType<typeof fetchFounderWorkspaceCounts>> | null>(null);
  const [plans, setPlans] = useState<FounderMembershipPlanRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<FounderSubscriptionRow[]>([]);
  const [payments, setPayments] = useState<FounderPaymentRow[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<FounderPaymentSummary | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<FounderPaymentRow | null>(null);
  const [priceSchedules, setPriceSchedules] = useState<FounderPriceScheduleRow[]>([]);
  const [pricingHistory, setPricingHistory] = useState<FounderMembershipPricingHistoryRow[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [planDraft, setPlanDraft] = useState<PlanDraft | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [paymentQuery, setPaymentQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(initialPaymentStatus);
  const [paymentProviderFilter, setPaymentProviderFilter] = useState("all");
  const [paymentFrom, setPaymentFrom] = useState("");
  const [paymentTo, setPaymentTo] = useState("");
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [newPlanName, setNewPlanName] = useState("");
  const [newPlanPrice, setNewPlanPrice] = useState("999");
  const [newPlanConfirmPassword, setNewPlanConfirmPassword] = useState("");
  const [interventionId, setInterventionId] = useState<string | null>(null);
  const [interventionDays, setInterventionDays] = useState("30");
  const [interventionReason, setInterventionReason] = useState("");
  const [publisherMembershipPriceDraft, setPublisherMembershipPriceDraft] = useState("");
  const [publisherMembershipPriceConfirm, setPublisherMembershipPriceConfirm] = useState("");
  const [publisherMembershipPriceSaving, setPublisherMembershipPriceSaving] = useState(false);
  const [razorpayProviderStatus, setRazorpayProviderStatus] = useState<
    Awaited<ReturnType<typeof fetchFounderPublisherPaymentProviderStatus>>["razorpay"] | null
  >(null);

  async function loadPricingHistory(planId: string) {
    try {
      const history = await fetchFounderMembershipPlanPricingHistory(planId);
      setPricingHistory(history);
    } catch {
      setPricingHistory([]);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const [workspaceCounts, planRows, subscriptionRows, paymentRows, summary, providerStatus] =
        await Promise.all([
        fetchFounderWorkspaceCounts(),
        fetchFounderMembershipPlans(),
        fetchFounderSubscriptions("active"),
        fetchFounderPayments({
          status: paymentStatusFilter,
          provider: paymentProviderFilter,
          from: paymentFrom,
          to: paymentTo,
        }),
        fetchFounderPaymentSummary(),
        fetchFounderPublisherPaymentProviderStatus(),
      ]);
      setCounts(workspaceCounts);
      setPlans(planRows);
      setSubscriptions(subscriptionRows);
      setPayments(paymentRows);
      setPaymentSummary(summary);
      setRazorpayProviderStatus(providerStatus.razorpay);
      setError("");
      const appAnnual =
        planRows.find((plan) => plan.slug === "app-annual") ?? planRows[0] ?? null;
      if (appAnnual) {
        setPublisherMembershipPriceDraft(String(Math.trunc(Number(appAnnual.price_amount))));
        await loadPricingHistory(appAnnual.id);
      }
    } catch {
      setError("Could not load membership data.");
    } finally {
      setLoading(false);
    }
  }

  async function reloadPayments(next?: {
    status?: string;
    provider?: string;
    from?: string;
    to?: string;
  }) {
    const status = next?.status ?? paymentStatusFilter;
    const provider = next?.provider ?? paymentProviderFilter;
    const from = next?.from ?? paymentFrom;
    const to = next?.to ?? paymentTo;
    try {
      const [paymentRows, summary] = await Promise.all([
        fetchFounderPayments({ status, provider, from, to }),
        fetchFounderPaymentSummary(),
      ]);
      setPayments(paymentRows);
      setPaymentSummary(summary);
    } catch {
      setMessage("Could not refresh payment records.");
      setMessageTone("error");
    }
  }

  useEffect(() => {
    load();
    // Initial workspace load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editingPlan = plans.find((plan) => plan.id === editingPlanId) ?? null;
  const primaryPlan =
    plans.find((plan) => plan.slug === "app-annual") ?? plans[0] ?? null;

  const planDirty = useMemo(() => {
    if (!editingPlan || !planDraft) {
      return false;
    }
    const original = toPlanDraft(editingPlan);
    return (
      original.name !== planDraft.name.trim() ||
      original.newPriceAmount !== planDraft.newPriceAmount ||
      original.renewalIncreasePercent !== planDraft.renewalIncreasePercent.trim() ||
      original.isActive !== planDraft.isActive ||
      original.effectiveFrom !== planDraft.effectiveFrom ||
      original.applyTo !== planDraft.applyTo
    );
  }, [editingPlan, planDraft]);

  const priceChanged = useMemo(() => {
    if (!editingPlan || !planDraft) {
      return false;
    }
    return planPriceValue(editingPlan) !== planDraft.newPriceAmount;
  }, [editingPlan, planDraft]);

  const renewalChanged = useMemo(() => {
    if (!editingPlan || !planDraft) {
      return false;
    }
    return String(renewalPercent(editingPlan)) !== planDraft.renewalIncreasePercent.trim();
  }, [editingPlan, planDraft]);

  const isFutureEffectiveDate = useMemo(() => {
    if (!planDraft?.effectiveFrom) {
      return false;
    }
    return planDraft.effectiveFrom > todayIsoDate();
  }, [planDraft]);

  const filteredPayments = useMemo(() => {
    const query = paymentQuery.trim().toLowerCase();
    return payments.filter((row) => {
      if (paymentStatusFilter !== "all" && row.status !== paymentStatusFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        row.publisher_name,
        row.publisher_email,
        row.application_name,
        row.status,
        row.provider,
        row.provider_reference,
        row.provider_order_id,
        row.payment_purpose,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [paymentQuery, paymentStatusFilter, payments]);

  function setFeedback(text: string, tone: "success" | "error" = "success") {
    setMessage(text);
    setMessageTone(tone);
  }

  async function loadPriceSchedules(planId: string) {
    try {
      const rows = await fetchFounderMembershipPlanPriceSchedules(planId);
      setPriceSchedules(rows);
    } catch {
      setPriceSchedules([]);
    }
  }

  function startEditPlan(plan: FounderMembershipPlanRow) {
    setEditingPlanId(plan.id);
    setPlanDraft(toPlanDraft(plan));
    setConfirmPublish(false);
    setMessage("");
    void Promise.all([loadPricingHistory(plan.id), loadPriceSchedules(plan.id)]);
  }

  function closeEditPlan() {
    setEditingPlanId(null);
    setPlanDraft(null);
    setConfirmPublish(false);
  }

  async function savePublisherMembershipPrice() {
    if (!primaryPlan) {
      setFeedback("No active Publisher Membership plan was found.", "error");
      return;
    }

    const validation = validatePublisherMembershipPriceInput(publisherMembershipPriceDraft);
    if (!validation.ok || validation.value == null) {
      setFeedback(validation.message, "error");
      return;
    }

    if (!publisherMembershipPriceConfirm.trim()) {
      setFeedback("Enter your current password to confirm this change.", "error");
      return;
    }

    const currentAmount = Math.trunc(Number(primaryPlan.price_amount));
    if (validation.value === currentAmount) {
      setFeedback("Publisher Membership Price is already set to this amount.", "error");
      return;
    }

    setPublisherMembershipPriceSaving(true);
    setMessage("");
    try {
      await publishFounderMembershipPlanPricing(primaryPlan.id, {
        priceAmount: validation.value,
        renewalIncreasePercent: Number(primaryPlan.renewal_increase_percent),
        effectiveDate: todayIsoDate(),
        applyScope: "new_memberships_only",
        reason: "Founder Publisher Membership Price update",
        confirmPassword: publisherMembershipPriceConfirm,
      });
      setPublisherMembershipPriceConfirm("");
      setFeedback("Publisher Membership Price saved.");
      await load();
    } catch (error) {
      const detail =
        error && typeof error === "object" && "response" in error
          ? String(
              (error as { response?: { data?: { message?: string } } }).response?.data
                ?.message ?? "",
            )
          : "";
      setFeedback(detail || "Could not save Publisher Membership Price.", "error");
    } finally {
      setPublisherMembershipPriceSaving(false);
    }
  }

  async function publishPlanUpdate(confirmPassword?: string) {
    if (!editingPlanId || !planDraft || !editingPlan) {
      return;
    }

    const newPriceValidation = validatePublisherMembershipPriceInput(planDraft.newPriceAmount);
    if (priceChanged && (!newPriceValidation.ok || newPriceValidation.value == null)) {
      setFeedback(newPriceValidation.message, "error");
      return;
    }
    const newPrice = newPriceValidation.value ?? Number(planDraft.newPriceAmount);
    const renewalValue = Number(planDraft.renewalIncreasePercent);
    if (!planDraft.name.trim() || Number.isNaN(newPrice) || newPrice < 0) {
      setFeedback("Enter a valid plan name and price.", "error");
      return;
    }
    if (Number.isNaN(renewalValue) || renewalValue < 0 || renewalValue > 100) {
      setFeedback("Enter a renewal increase between 0 and 100.", "error");
      return;
    }

    setSubmitting(true);
    setMessage("");
    try {
      if (priceChanged || renewalChanged) {
        if (!confirmPassword) {
          setFeedback("Enter your current password to confirm this change.", "error");
          setSubmitting(false);
          return;
        }
        await publishFounderMembershipPlanPricing(editingPlanId, {
          priceAmount: priceChanged ? newPrice : undefined,
          renewalIncreasePercent: renewalChanged ? renewalValue : undefined,
          effectiveDate: planDraft.effectiveFrom,
          applyScope: planDraft.applyTo,
          confirmPassword,
        });
      }

      const updated = await updateFounderMembershipPlan(editingPlanId, {
        name: planDraft.name.trim(),
        isActive: planDraft.isActive,
        ...(renewalChanged && !priceChanged ? { renewalIncreasePercent: renewalValue } : {}),
      });
      setPlans((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      await Promise.all([loadPricingHistory(updated.id), loadPriceSchedules(updated.id)]);

      if (priceChanged && isFutureEffectiveDate) {
        setFeedback(`Price change scheduled for ${formatDate(planDraft.effectiveFrom)}.`);
      } else if (priceChanged && renewalChanged) {
        setFeedback(`Price and renewal rule updated for ${updated.name}.`);
      } else if (priceChanged) {
        setFeedback(`Price updated to ${formatPlanPrice(newPrice, updated.currency)}.`);
      } else if (renewalChanged) {
        setFeedback(`Renewal increase updated to ${renewalValue}%.`);
      } else {
        setFeedback("Membership plan updated.");
      }
      closeEditPlan();
    } catch {
      setFeedback("Could not update membership plan.", "error");
    } finally {
      setSubmitting(false);
      setConfirmPublish(false);
    }
  }

  const metrics = [
    ...(counts
      ? [
          { id: "active", label: "Active memberships", value: counts.activeSubscriptions, tone: "teal" as const },
          { id: "plans", label: "Membership plans", value: plans.length, tone: "steel" as const },
        ]
      : []),
    ...(paymentSummary
      ? [
          {
            id: "paid",
            label: "Successful payments",
            value: paymentSummary.succeededCount,
            tone: "mint" as const,
          },
          {
            id: "pend",
            label: "Pending payments",
            value: paymentSummary.pendingCount,
            tone: "sand" as const,
          },
          {
            id: "fail",
            label: "Failed payments",
            value: paymentSummary.failedCount,
            tone: paymentSummary.failedCount > 0 ? ("coral" as const) : ("sand" as const),
          },
        ]
      : counts
        ? [
            {
              id: "fail",
              label: "Payment issues",
              value: counts.paymentFailures,
              tone: counts.paymentFailures > 0 ? ("coral" as const) : ("sand" as const),
            },
          ]
        : []),
  ];

  return (
    <FounderOperationalShell metrics={metrics} title="Memberships & Payments">
      {loading ? <FounderLoadingState label="Loading memberships..." /> : null}
      {!loading && error ? <FounderErrorState message={error} /> : null}

      {!loading && !error ? (
        <div className="oac-founder-ops-grid oac-founder-ops-grid--2col">
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Active memberships</h2>
            {subscriptions.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">No active memberships right now.</p>
            ) : (
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {subscriptions.slice(0, 12).map((row) => (
                  <li className="oac-founder-ops-list__item" key={row.id}>
                    <div>
                      <span className="oac-founder-ops-list__title">{row.application_name}</span>
                      <span className="oac-founder-ops-list__meta">
                        {row.publisher_name} · Expires {formatDate(row.expires_at)}
                      </span>
                    </div>
                    <div className="oac-founder-ops-list__actions">
                      <span className="oac-founder-badge oac-founder-badge--healthy">{row.status}</span>
                      <FounderIconButton
                        kind="edit"
                        label={`Extend membership for ${row.application_name}`}
                        onClick={() => setInterventionId(row.id)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="oac-founder-ops-panel">
            <div className="oac-founder-ops-panel__head">
              <h2 className="oac-founder-ops-panel__title">Publisher Membership Price</h2>
            </div>
            <p className="oac-founder-ops-panel__lead">
              Current price:{" "}
              <strong>
                {primaryPlan
                  ? formatPlanPrice(primaryPlan.price_amount, primaryPlan.currency)
                  : "—"}
              </strong>
            </p>
            <div className="oac-founder-ops-inline-form">
              <div className="oac-founder-field">
                <label htmlFor="publisher-membership-price">New price (INR)</label>
                <input
                  id="publisher-membership-price"
                  inputMode="numeric"
                  onChange={(e) => setPublisherMembershipPriceDraft(e.target.value)}
                  placeholder="999"
                  value={publisherMembershipPriceDraft}
                />
              </div>
              <div className="oac-founder-field">
                <label htmlFor="publisher-membership-price-confirm">Confirm password</label>
                <input
                  autoComplete="current-password"
                  id="publisher-membership-price-confirm"
                  onChange={(e) => setPublisherMembershipPriceConfirm(e.target.value)}
                  type="password"
                  value={publisherMembershipPriceConfirm}
                />
              </div>
              <FounderIconButton
                disabled={publisherMembershipPriceSaving}
                kind="publish"
                label={publisherMembershipPriceSaving ? "Saving…" : "Save"}
                onClick={() => {
                  void savePublisherMembershipPrice();
                }}
              />
            </div>
            <p className="oac-founder-ops-footnote">
              Whole rupees only. This value becomes the Membership screen and checkout price for
              new memberships. Already-paid periods stay unchanged.
            </p>
          </section>

          <section className="oac-founder-ops-panel">
            <div className="oac-founder-ops-panel__head">
              <h2 className="oac-founder-ops-panel__title">Razorpay</h2>
            </div>
            <p className="oac-founder-ops-panel__lead">
              Status:{" "}
              <span
                className={`oac-founder-badge oac-founder-badge--${
                  razorpayProviderStatus?.available ? "healthy" : "attention"
                }`}
              >
                {razorpayProviderStatus?.available ? "Active" : "Unavailable"}
              </span>
            </p>
            <p className="oac-founder-ops-footnote">
              Publisher membership payments use Razorpay when credentials and provider availability
              are configured.
            </p>
          </section>

          <section className="oac-founder-ops-panel">
            <div className="oac-founder-ops-panel__head">
              <h2 className="oac-founder-ops-panel__title">Membership plans</h2>
              <FounderIconButton
                kind="edit"
                label="Create membership plan"
                onClick={() => setCreatingPlan(true)}
              />
            </div>
            {primaryPlan ? (
              <p className="oac-founder-ops-panel__lead">
                Renewal increase: <strong>{renewalPercent(primaryPlan)}%</strong> per year
              </p>
            ) : null}
            {plans.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">No membership plans found.</p>
            ) : (
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {plans.map((plan) => (
                  <li className="oac-founder-ops-list__item" key={plan.id}>
                    <div>
                      <span className="oac-founder-ops-list__title">{plan.name}</span>
                      <span className="oac-founder-ops-list__meta">
                        {formatPlanPrice(plan.price_amount, plan.currency)}
                      </span>
                    </div>
                    <div className="oac-founder-ops-list__actions">
                      <span
                        className={
                          plan.is_active
                            ? "oac-founder-badge oac-founder-badge--healthy"
                            : "oac-founder-badge oac-founder-badge--neutral"
                        }
                      >
                        {plan.is_active ? "Active" : "Inactive"}
                      </span>
                      <FounderIconButton
                        kind="edit"
                        label={`Edit ${plan.name}`}
                        onClick={() => startEditPlan(plan)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <details
              className="oac-founder-pricing-history-compact"
              onToggle={(event) => setHistoryOpen(event.currentTarget.open)}
              open={historyOpen}
            >
              <summary className="oac-founder-pricing-history-compact__summary">Pricing history</summary>
              {pricingHistory.length === 0 ? (
                <p className="oac-founder-ops-panel__empty">No previous pricing changes</p>
              ) : (
                <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                  {pricingHistory.slice(0, 8).map((entry) => (
                    <li className="oac-founder-ops-list__item" key={entry.id}>
                      <div>
                        <span className="oac-founder-ops-list__title">
                          {entry.previousPrice === null
                            ? formatPlanPrice(entry.newPrice, primaryPlan?.currency ?? "INR")
                            : `${formatPlanPrice(entry.previousPrice, primaryPlan?.currency ?? "INR")} → ${formatPlanPrice(entry.newPrice, primaryPlan?.currency ?? "INR")}`}
                        </span>
                        <span className="oac-founder-ops-list__meta">{formatDateTime(entry.changedAt)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </details>

            {message ? (
              <p
                className={
                  messageTone === "error"
                    ? "oac-founder-ops-footnote"
                    : "oac-founder-ops-footnote oac-founder-ops-footnote--success"
                }
              >
                {message}
              </p>
            ) : null}
          </section>

          <section className="oac-founder-ops-panel oac-founder-ops-panel--wide">
            <h2 className="oac-founder-ops-panel__title">Payments & renewals</h2>
            {paymentSummary ? (
              <ul className="oac-founder-kv">
                <li>
                  <span>Membership revenue (successful)</span>
                  <strong>{formatPlanPrice(paymentSummary.membershipRevenue, paymentSummary.currency)}</strong>
                </li>
                <li>
                  <span>Renewal revenue (successful)</span>
                  <strong>{formatPlanPrice(paymentSummary.renewalRevenue, paymentSummary.currency)}</strong>
                </li>
              </ul>
            ) : null}
            <div className="oac-founder-ops-inline-form oac-founder-payment-filters">
              <input
                aria-label="Search publisher, email, app, or reference"
                className="oac-founder-ops-search"
                onChange={(e) => setPaymentQuery(e.target.value)}
                placeholder="Search publisher, email, app, or reference"
                type="search"
                value={paymentQuery}
              />
              <select
                aria-label="Filter by payment status"
                className="oac-founder-ops-search"
                onChange={(e) => {
                  const value = e.target.value;
                  setPaymentStatusFilter(value);
                  void reloadPayments({ status: value });
                }}
                value={paymentStatusFilter}
              >
                <option value="all">All statuses</option>
                <option value="succeeded">Successful</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                aria-label="Filter by payment provider"
                className="oac-founder-ops-search"
                onChange={(e) => {
                  const value = e.target.value;
                  setPaymentProviderFilter(value);
                  void reloadPayments({ provider: value });
                }}
                value={paymentProviderFilter}
              >
                <option value="all">All providers</option>
                <option value="razorpay">Razorpay</option>
                <option value="stripe">Stripe</option>
              </select>
              <input
                aria-label="From date"
                className="oac-founder-ops-search"
                onChange={(e) => {
                  const value = e.target.value;
                  setPaymentFrom(value);
                  void reloadPayments({ from: value });
                }}
                type="date"
                value={paymentFrom}
              />
              <input
                aria-label="To date"
                className="oac-founder-ops-search"
                onChange={(e) => {
                  const value = e.target.value;
                  setPaymentTo(value);
                  void reloadPayments({ to: value });
                }}
                type="date"
                value={paymentTo}
              />
            </div>
            {filteredPayments.length === 0 ? (
              <p className="oac-founder-ops-panel__empty">No payment records match this view.</p>
            ) : (
              <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                {filteredPayments.map((row) => (
                  <li className="oac-founder-ops-list__item" key={row.id}>
                    <button
                      className="oac-founder-payment-row"
                      onClick={() => setSelectedPayment(row)}
                      type="button"
                    >
                      <span className="oac-founder-ops-list__title">{row.publisher_name}</span>
                      <span className="oac-founder-ops-list__meta">
                        {unavailable(row.publisher_email)} · {unavailable(row.application_name)} ·{" "}
                        {paymentPurposeLabel(row.payment_purpose)} ·{" "}
                        {formatPlanPrice(row.amount, row.currency)} · {formatDateTime(row.created_at)}
                      </span>
                    </button>
                    <span
                      className={
                        row.status === "succeeded"
                          ? "oac-founder-badge oac-founder-badge--healthy"
                          : row.status === "failed"
                            ? "oac-founder-badge oac-founder-badge--problem"
                            : "oac-founder-badge oac-founder-badge--neutral"
                      }
                    >
                      {paymentStatusLabel(row.status)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}

      {selectedPayment ? (
        <FounderEditDrawer
          footer={
            <div className="oac-founder-drawer__actions">
              <FounderIconButton kind="cancel" label="Close" onClick={() => setSelectedPayment(null)} />
            </div>
          }
          onClose={() => setSelectedPayment(null)}
          open={Boolean(selectedPayment)}
          title="Payment record"
        >
          <ul className="oac-founder-kv">
            <li>
              <span>Publisher</span>
              <strong>{unavailable(selectedPayment.publisher_name)}</strong>
            </li>
            <li>
              <span>Email</span>
              <strong>{unavailable(selectedPayment.publisher_email)}</strong>
            </li>
            <li>
              <span>App</span>
              <strong>{unavailable(selectedPayment.application_name)}</strong>
            </li>
            <li>
              <span>Membership plan</span>
              <strong>{unavailable(selectedPayment.plan_name)}</strong>
            </li>
            <li>
              <span>Payment type</span>
              <strong>{paymentPurposeLabel(selectedPayment.payment_purpose)}</strong>
            </li>
            <li>
              <span>Amount</span>
              <strong>{formatPlanPrice(selectedPayment.amount, selectedPayment.currency)}</strong>
            </li>
            <li>
              <span>Provider</span>
              <strong>{unavailable(selectedPayment.provider)}</strong>
            </li>
            <li>
              <span>Status</span>
              <strong>{paymentStatusLabel(selectedPayment.status)}</strong>
            </li>
            <li>
              <span>Transaction reference</span>
              <strong>{unavailable(selectedPayment.provider_reference)}</strong>
            </li>
            <li>
              <span>Order / checkout ID</span>
              <strong>{unavailable(selectedPayment.provider_order_id)}</strong>
            </li>
            <li>
              <span>Payment date</span>
              <strong>{formatDateTime(selectedPayment.created_at)}</strong>
            </li>
            <li>
              <span>Verified at</span>
              <strong>
                {selectedPayment.verified_at
                  ? formatDateTime(selectedPayment.verified_at)
                  : "Not recorded"}
              </strong>
            </li>
            <li>
              <span>Membership status</span>
              <strong>{unavailable(selectedPayment.membership_status)}</strong>
            </li>
            <li>
              <span>Membership start</span>
              <strong>
                {selectedPayment.membership_starts_at
                  ? formatDate(selectedPayment.membership_starts_at)
                  : "Not recorded"}
              </strong>
            </li>
            <li>
              <span>Membership expiry / renewal due</span>
              <strong>
                {selectedPayment.membership_expires_at
                  ? formatDate(selectedPayment.membership_expires_at)
                  : "Not recorded"}
              </strong>
            </li>
            <li>
              <span>Renewal count</span>
              <strong>
                {selectedPayment.renewal_count === null || selectedPayment.renewal_count === undefined
                  ? "Not recorded"
                  : String(selectedPayment.renewal_count)}
              </strong>
            </li>
            <li>
              <span>Payment method</span>
              <strong>Not recorded</strong>
            </li>
          </ul>
        </FounderEditDrawer>
      ) : null}

      {creatingPlan ? (
        <FounderEditDrawer
          footer={
            <div className="oac-founder-drawer__actions">
              <FounderIconButton
                kind="publish"
                label="Create plan"
                onClick={async () => {
                  if (!newPlanConfirmPassword.trim()) {
                    setFeedback("Enter your current password to create a plan.", "error");
                    return;
                  }
                  try {
                    const created = await createFounderMembershipPlan({
                      name: newPlanName.trim(),
                      priceAmount: Number(newPlanPrice),
                      renewalIncreasePercent: 15,
                      isActive: true,
                      confirmPassword: newPlanConfirmPassword,
                    });
                    setPlans((rows) => [created, ...rows]);
                    setCreatingPlan(false);
                    setNewPlanName("");
                    setNewPlanPrice("999");
                    setNewPlanConfirmPassword("");
                    setFeedback(`Plan ${created.name} created.`);
                  } catch {
                    setFeedback("Could not create membership plan.", "error");
                  }
                }}
              />
              <FounderIconButton kind="cancel" label="Cancel" onClick={() => setCreatingPlan(false)} />
            </div>
          }
          onClose={() => setCreatingPlan(false)}
          open={creatingPlan}
          title="Create membership plan"
        >
          <div className="oac-founder-field">
            <label htmlFor="new-plan-name">Plan name</label>
            <input id="new-plan-name" onChange={(e) => setNewPlanName(e.target.value)} value={newPlanName} />
          </div>
          <div className="oac-founder-field">
            <label htmlFor="new-plan-price">Price</label>
            <input id="new-plan-price" onChange={(e) => setNewPlanPrice(e.target.value)} value={newPlanPrice} />
          </div>
          <div className="oac-founder-field">
            <label htmlFor="new-plan-confirm-password">Current password</label>
            <input
              autoComplete="current-password"
              id="new-plan-confirm-password"
              onChange={(e) => setNewPlanConfirmPassword(e.target.value)}
              type="password"
              value={newPlanConfirmPassword}
            />
          </div>
        </FounderEditDrawer>
      ) : null}

      {interventionId ? (
        <FounderEditDrawer
          footer={
            <div className="oac-founder-drawer__actions">
              <FounderIconButton
                kind="publish"
                label="Apply extension"
                onClick={async () => {
                  await interveneFounderSubscription(interventionId, {
                    action: "extend",
                    days: Number(interventionDays),
                    reason: interventionReason.trim(),
                  });
                  setInterventionId(null);
                  setInterventionReason("");
                  await load();
                  setFeedback("Membership extended.");
                }}
              />
              <FounderIconButton kind="cancel" label="Cancel" onClick={() => setInterventionId(null)} />
            </div>
          }
          onClose={() => setInterventionId(null)}
          open={Boolean(interventionId)}
          title="Membership support"
        >
          <div className="oac-founder-field">
            <label htmlFor="intervention-days">Extend by (days)</label>
            <input
              id="intervention-days"
              onChange={(e) => setInterventionDays(e.target.value)}
              value={interventionDays}
            />
          </div>
          <div className="oac-founder-field">
            <label htmlFor="intervention-reason">Reason</label>
            <textarea
              id="intervention-reason"
              onChange={(e) => setInterventionReason(e.target.value)}
              rows={3}
              value={interventionReason}
            />
          </div>
        </FounderEditDrawer>
      ) : null}

      <FounderEditDrawer
        footer={
          <div className="oac-founder-drawer__actions">
            {planDirty ? (
              <FounderIconButton
                kind="publish"
                label="Publish update"
                onClick={() => setConfirmPublish(true)}
              />
            ) : null}
            <FounderIconButton kind="cancel" label="Cancel edit" onClick={closeEditPlan} />
          </div>
        }
        onClose={closeEditPlan}
        open={Boolean(editingPlan && planDraft)}
        title={editingPlan ? `Edit ${editingPlan.name}` : "Edit plan"}
      >
        {editingPlan && planDraft ? (
          <>
            <div className="oac-founder-field">
              <label htmlFor="founder-plan-name">Plan name</label>
              <input
                id="founder-plan-name"
                onChange={(e) => setPlanDraft({ ...planDraft, name: e.target.value })}
                value={planDraft.name}
              />
            </div>
            <div className="oac-founder-field">
              <label htmlFor="founder-plan-current-price">Current price</label>
              <input
                disabled
                id="founder-plan-current-price"
                readOnly
                value={formatPlanPrice(editingPlan.price_amount, editingPlan.currency)}
              />
            </div>
            <div className="oac-founder-field">
              <label htmlFor="founder-plan-new-price">New price</label>
              <input
                id="founder-plan-new-price"
                inputMode="decimal"
                onChange={(e) => setPlanDraft({ ...planDraft, newPriceAmount: e.target.value })}
                value={planDraft.newPriceAmount}
              />
            </div>
            <div className="oac-founder-field">
              <label htmlFor="founder-plan-effective-from">Effective from</label>
              <input
                id="founder-plan-effective-from"
                min={todayIsoDate()}
                onChange={(e) => setPlanDraft({ ...planDraft, effectiveFrom: e.target.value })}
                type="date"
                value={planDraft.effectiveFrom}
              />
            </div>
            <fieldset className="oac-founder-pricing-fieldset oac-founder-pricing-fieldset--compact">
              <legend>Applies to</legend>
              <label className="oac-founder-ops-radio">
                <input
                  checked={planDraft.applyTo === "new_memberships_only"}
                  name="founder-plan-apply-to"
                  onChange={() => setPlanDraft({ ...planDraft, applyTo: "new_memberships_only" })}
                  type="radio"
                />
                New memberships only
              </label>
              <label className="oac-founder-ops-radio">
                <input
                  checked={planDraft.applyTo === "new_and_renewals"}
                  name="founder-plan-apply-to"
                  onChange={() => setPlanDraft({ ...planDraft, applyTo: "new_and_renewals" })}
                  type="radio"
                />
                New memberships + future renewals
              </label>
            </fieldset>
            <div className="oac-founder-field">
              <label htmlFor="founder-renewal-percent">Renewal increase (%)</label>
              <input
                id="founder-renewal-percent"
                inputMode="decimal"
                onChange={(e) =>
                  setPlanDraft({ ...planDraft, renewalIncreasePercent: e.target.value })
                }
                value={planDraft.renewalIncreasePercent}
              />
            </div>
            <label className="oac-founder-ops-radio">
              <input
                checked={planDraft.isActive}
                onChange={(e) => setPlanDraft({ ...planDraft, isActive: e.target.checked })}
                type="checkbox"
              />
              Plan active
            </label>
            {priceSchedules.filter((row) => row.status === "scheduled").length > 0 ? (
              <div className="oac-founder-field">
                <p className="oac-founder-ops-list__title">Scheduled price changes</p>
                <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                  {priceSchedules
                    .filter((row) => row.status === "scheduled")
                    .map((row) => (
                      <li className="oac-founder-ops-list__item" key={row.id}>
                        <div>
                          <span className="oac-founder-ops-list__meta">
                            {formatPlanPrice(row.price_amount, editingPlan.currency)} from{" "}
                            {formatDate(String(row.effective_date))}
                          </span>
                        </div>
                        <FounderIconButton
                          kind="cancel"
                          label="Cancel scheduled change"
                          onClick={async () => {
                            await cancelFounderMembershipPlanPriceSchedule(row.id);
                            await loadPriceSchedules(editingPlan.id);
                            setFeedback("Scheduled price change cancelled.");
                          }}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}
            {confirmPublish ? (
              <FounderConfirmPublish
                message={
                  priceChanged
                    ? `Publish ${formatPlanPrice(planDraft.newPriceAmount, editingPlan.currency)} for ${planDraft.name}? Already-paid membership periods stay unchanged.`
                    : `Publish changes to ${planDraft.name}?`
                }
                onCancel={() => setConfirmPublish(false)}
                onConfirm={publishPlanUpdate}
                requirePassword={priceChanged || renewalChanged}
                submitting={submitting}
              />
            ) : null}
          </>
        ) : null}
      </FounderEditDrawer>
    </FounderOperationalShell>
  );
}
